import { Button, Form, InputNumber, Modal, Select, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { CURRENCY_LIST } from '../constants';
import { ICurrency, ICurrencyForm } from '../types';

const { Text } = Typography;

interface AddEditExchangeRateModalProps {
  open: boolean;
  editingRecord: ICurrency | null;
  isLoading: boolean;
  onCancel: () => void;
  onSave: (values: ICurrencyForm) => void;
}

export const AddEditExchangeRateModal: React.FC<AddEditExchangeRateModalProps> = ({
  open,
  editingRecord,
  isLoading,
  onCancel,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    editingRecord?.dogma_isocurrencycode
  );

  useEffect(() => {
    if (open) {
      if (editingRecord) {
        form.setFieldsValue({
          dogma_isocurrencycode: editingRecord.dogma_isocurrencycode,
          dogma_exchangerate: editingRecord.dogma_exchangerate,
          dogma_vatpercentage: editingRecord.dogma_vatpercentage,
        });
        setSelectedCurrency(editingRecord.dogma_isocurrencycode);
      } else {
        form.resetFields();
        setSelectedCurrency(undefined);
      }
    }
  }, [open, editingRecord, form]);

  const handleCurrencySelect = (code: string) => {
    const selected = CURRENCY_LIST.find((c) => c.code === code);
    if (selected) {
      form.setFieldValue('dogma_isocurrencycode', selected.code);
      setSelectedCurrency(selected.code);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const selected = CURRENCY_LIST.find((c) => c.code === values.dogma_isocurrencycode);
      const payload: ICurrencyForm = {
        dogma_name: selected?.name ?? values.dogma_isocurrencycode,
        dogma_isocurrencycode: values.dogma_isocurrencycode,
        dogma_currencysymbol: selected?.symbol ?? values.dogma_isocurrencycode,
        dogma_exchangerate: values.dogma_exchangerate,
        dogma_vatpercentage: values.dogma_vatpercentage,
      };
      onSave(payload);
    } catch {
      // validation errors shown inline
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={editingRecord ? 'Edit Currency' : 'Add Currency'}
      open={open}
      onCancel={handleCancel}
      width={560}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={isLoading}>
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          label="Currency"
          name="dogma_isocurrencycode"
          rules={[{ required: true, message: 'Please select a currency' }]}
        >
          <Select
            showSearch
            placeholder="Search by name or ISO code"
            optionFilterProp="label"
            onChange={handleCurrencySelect}
            options={CURRENCY_LIST.map((c) => ({
              value: c.code,
              label: `${c.name} (${c.code})`,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Exchange Rate"
          name="dogma_exchangerate"
          rules={[{ required: true, message: 'Please enter the exchange rate' }]}
          extra={
            <Text type="secondary" className="text-xs">
              Enter how many <strong>{selectedCurrency ?? 'foreign currency units'}</strong> equal <strong>1 GBP</strong>.
              {selectedCurrency
                ? <span> e.g. if 1 GBP = 1.18 {selectedCurrency}, enter <strong>1.18</strong></span>
                : <span> e.g. for EUR enter <strong>1.18</strong> if 1 GBP = 1.18 EUR</span>
              }
            </Text>
          }
        >
          <InputNumber
            className="w-full"
            min={0.000001}
            step={0.0001}
            placeholder="e.g. 1.18"
          />
        </Form.Item>

        <Form.Item
          label="VAT Percentage"
          name="dogma_vatpercentage"
          rules={[{ required: true, message: 'Please enter the VAT percentage' }]}
        >
          <InputNumber className="w-full" min={0} max={100} step={0.01} placeholder="e.g. 20" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
